/**
 * O botão "Exame" da barra da aba Exame (E205.F2.T03, ADR-0147 §3): as três saídas do mesmo exame
 * num lugar só — "Ver prévia (PDF)" (o painel ao lado, na prévia que a preparação guardou), "Baixar
 * exame" e "Baixar exame e arquivos (ZIP)". Antes eram três botões em dois lugares (o topo e a linha do
 * pacote). O menu é um `details`, como o "Mais" do cabeçalho; cada escolha o fecha.
 *
 * O exame que ainda não sai diz o porquê no lugar do item (o motivo de sempre, `baixar-exame-motivo`).
 */
import { ChevronDown } from 'lucide-react';
import { useRef } from 'react';

import { motivoDoExame, type DownloadDoExame } from '@/components/ExameEletronico/BaixarExame';
import type { DownloadDoZip } from '@/components/PreparacaoDoPacote/BaixarExameEArquivos';
import { buttonVariants } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { DisponibilidadeDoExame } from '@/lib/exame_eletronico_saida';
import { cn } from '@/lib/utils';

const t = ptBR.mesaUnica.exame;
const tb = ptBR.exameEletronico.baixarExame;
const tz = ptBR.preparacaoDoPacote;

const ITEM =
  'block w-full px-sc-3 py-sc-2 text-left text-body-sm text-fg hover:bg-fg/[0.05] disabled:cursor-not-allowed disabled:opacity-45';

export interface MenuDoExameProps {
  onPrevia: () => void;
  exame: DownloadDoExame;
  disponibilidade: DisponibilidadeDoExame;
  zip: DownloadDoZip;
}

export function MenuDoExame({
  onPrevia,
  exame,
  disponibilidade,
  zip,
}: MenuDoExameProps): JSX.Element {
  const motivo = motivoDoExame(disponibilidade);
  const ref = useRef<HTMLDetailsElement>(null);
  const escolher = (acao: () => void) => () => {
    if (ref.current !== null) ref.current.open = false;
    acao();
  };
  return (
    <details ref={ref} className="relative" data-testid="mesa-acao-exame">
      <summary
        className={cn(
          buttonVariants({ variant: 'primary', size: 'sm' }),
          'cursor-pointer list-none gap-1',
        )}
        aria-label={t.rotulo}
        data-testid="mesa-acao-exame-abrir"
      >
        {t.botao}
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </summary>
      <div
        className="absolute right-0 z-20 mt-sc-1 min-w-[16rem] border border-border bg-bg py-sc-1 shadow-sm"
        data-testid="mesa-acao-exame-menu"
      >
        <button
          type="button"
          className={ITEM}
          onClick={escolher(onPrevia)}
          data-testid="mesa-acao-previa"
        >
          {t.previa}
        </button>
        {motivo === null ? (
          <button
            type="button"
            className={ITEM}
            disabled={exame.baixando}
            onClick={escolher(exame.baixar)}
            data-testid="baixar-exame-acao"
          >
            {exame.baixando ? tb.baixando : tb.acao}
          </button>
        ) : (
          <p
            className="px-sc-3 py-sc-2 text-caption text-fg-muted"
            data-testid="baixar-exame-motivo"
            data-motivo={disponibilidade.disponivel ? undefined : disponibilidade.motivo}
          >
            <span className="block text-body-sm text-fg-dim">{tb.acao}</span>
            {motivo}
          </p>
        )}
        <button
          type="button"
          className={ITEM}
          disabled={zip.baixando}
          onClick={escolher(zip.baixar)}
          title={tz.exameEArquivos.dica}
          data-testid="baixar-exame-e-arquivos"
        >
          {zip.baixando ? tz.baixando : tz.exameEArquivos.baixar}
        </button>
      </div>
    </details>
  );
}
